import React, { useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import Modal from 'components/modal/modal'
import ReCAPTCHA from 'react-google-recaptcha'
import { Col, ModalBody, ModalHeader, ModalTitle, Row } from 'react-bootstrap'
import { InfoButton } from 'components/buttons'
import {
  ChecklistIcon,
  ShippingIcon,
  ArrowRightIcon,
  OtherStoresIcon,
  ReturnToBabylistIcon,
} from 'components/icons'
import { getRegistry } from 'cart/reducers'
import {
  getIsShippingAddressPrivate,
  getShippingAddress,
} from 'registry/reducers'
import { handleAmazonRemoteCartCheckout } from 'cart/lib'
import { RegistryShape, ReservationShape } from 'cart/lib/prop-types'
import Link from 'components/link'
import Spinner from 'shared/spinner'
import classnames from 'classnames'
// eslint-disable-next-line import/no-unresolved
import FTCDisclosureTag from 'components/FTCDisclosureTag'
import { setIsShippingAddressButtonVisible } from 'registry/actions'
import { RECAPTCHA_V3_SITE_KEY } from 'shared/constants'
import AddressDoesNotTransferDisclaimer from 'registry/components/modals/ContactInfoModal/AddressDoesNotTransferDisclaimer'
import {
  NoAddressProvidedDisclosure,
  useContactInfoAddress,
  // eslint-disable-next-line import/no-unresolved
} from '../ContactInfoModal'
import css from './redirect-modal.scss'

export const BaseRedirectModal = ({
  isShippingAddressPrivate,
  isShopRegistryView,
  onHide,
  onRedirectSuccess,
  registry,
  reservations,
  setIsShippingAddressButtonVisible,
  storeDisplayName,
  url,
}) => {
  const recaptchaRef = React.useRef(null)
  const [showAddress, setShowAddress] = useState(false)
  const { data: contactInfo, isLoading } = useContactInfoAddress({
    addressId: registry.addressId,
    recaptchaRef,
    isShippingAddressPrivate,
  })

  function handleRedirect() {
    if (storeDisplayName === 'Amazon' && reservations?.length > 1) {
      // Amazon grouped reservations
      handleAmazonRemoteCartCheckout(reservations)
    } else if (url) {
      window.open(url)
    }

    onHide()
    onRedirectSuccess()
    setIsShippingAddressButtonVisible(true)
  }

  const continueToStoreButton = (
    <InfoButton className={css.continueButton} onClick={handleRedirect} pill>
      Continue to {storeDisplayName}
    </InfoButton>
  )

  if (isShopRegistryView) {
    return (
      <Modal size="small" show>
        <ModalHeader
          closeButton
          onHide={onHide}
          className={classnames(css.headerContent, 'with-title')}
        >
          <ModalTitle className="h3" style={{ textAlign: 'center' }}>
            You're headed to {storeDisplayName} 🚀
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="flex col center">
            <div className="flex" style={{ justifyContent: 'center' }}>
              <ReturnToBabylistIcon className={css.icon} />
              <ArrowRightIcon />
              <OtherStoresIcon className={css.icon} />
            </div>
            <div className="mbm">
              <p className={classnames(css.instructionText, 'text-center h5')}>
                <strong>If you buy at {storeDisplayName},</strong> return to
                Babylist and click `Purchased` to update your registry
              </p>
            </div>
            <div className="mvxl">{continueToStoreButton}</div>
          </div>
        </ModalBody>
      </Modal>
    )
  }

  return (
    <Modal size="small" show>
      <ModalHeader closeButton onHide={onHide} className="with-title">
        <ModalTitle className="h3" style={{ textAlign: 'center' }}>
          You're headed to {storeDisplayName}&#8230;
        </ModalTitle>
      </ModalHeader>

      <ModalBody>
        <Row>
          <Col xs={12} sm={6}>
            <div className="flex col align-center mbxl">
              <ChecklistIcon width={77} className="mbl" />

              <p className="text-center mhl">
                After purchase, return to Babylist and click{' '}
                <strong>I've Purchased This</strong>
              </p>
            </div>
          </Col>

          <Col xs={12} sm={6}>
            <div className="flex col align-center mbl">
              <ShippingIcon width={77} className="mbl" />

              {/* eslint-disable-next-line no-nested-ternary */}
              {isLoading ? (
                <Spinner />
              ) : contactInfo ? (
                <>
                  {!showAddress ? (
                    <p className="text-center mhl">
                      <strong>
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <Link onClick={() => setShowAddress(true)}>
                          Need {registry.ownerFirstName}'s shipping address?
                        </Link>
                      </strong>
                    </p>
                  ) : (
                    <div>
                      <AddressDoesNotTransferDisclaimer />
                      <div className="phl mtm mlm">
                        {contactInfo.fullName}
                        <br />
                        {contactInfo.address.streetAddress1}{' '}
                        {contactInfo.address.streetAddress2 ? (
                          <span>, {contactInfo.address.streetAddress2}</span>
                        ) : null}
                        <br />
                        {contactInfo.address.city}, {contactInfo.address.state}{' '}
                        {contactInfo.address.postalCode}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <NoAddressProvidedDisclosure name={registry.ownerFirstName} />
              )}
            </div>
          </Col>
        </Row>

        <div className="flex center col mtl">
          {continueToStoreButton}

          <FTCDisclosureTag
            className="mtl"
            disclaimerContext={FTCDisclosureTag.Constants.MODAL}
          />
        </div>
      </ModalBody>

      <div style={{ visibility: 'hidden' }}>
        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          sitekey={RECAPTCHA_V3_SITE_KEY}
        />
      </div>
    </Modal>
  )
}

BaseRedirectModal.propTypes = {
  onHide: PropTypes.func.isRequired,
  onRedirectSuccess: PropTypes.func,
  registry: PropTypes.shape(RegistryShape),
  reservations: PropTypes.arrayOf(PropTypes.shape(ReservationShape)),
  isShopRegistryView: PropTypes.bool,
  storeDisplayName: PropTypes.string,
  url: PropTypes.string,
  setIsShippingAddressButtonVisible: PropTypes.bool,
  isShippingAddressPrivate: PropTypes.bool,
}

BaseRedirectModal.defaultProps = {
  onRedirectSuccess: () => {},
  registry: null,
  reservations: null,
  isShopRegistryView: false,
  storeDisplayName: 'the store',
  url: undefined,
  setIsShippingAddressButtonVisible: false,
  isShippingAddressPrivate: false,
}

const mapStateToProps = (state) => ({
  registry: getRegistry(state),
  shippingAddress: getShippingAddress(state),
  isShippingAddressPrivate: !!getIsShippingAddressPrivate(state),
})

const mapDispatchToProps = (dispatch) => ({
  setIsShippingAddressButtonVisible: (props) =>
    dispatch(setIsShippingAddressButtonVisible(props)),
})

export const RedirectModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(BaseRedirectModal)
